// AutomotiveClientPage.jsx
// ---------------------------------------------------------
// FIXED: compiles cleanly, CSS module import, robust avatar
// ---------------------------------------------------------

import React from 'react';
// FIX: import CSS Module as a namespace so `s.*` works
import s from './AutomotiveClientPage.module.css';
import useSeo from '../../helpers/useSeo';

// Assets
import auto_header from '../../assets/auto_header.png';
import blueCadillac from '../../assets/blueCadillac.jpg';
import benHeadshot from '../../assets/Ben.jpg';
import carsonHeadshot from '../../assets/Carson.jpg';
import dylanHeadshot from '../../assets/Dylan.jpg';
import caliHeadshot from '../../assets/Cali.jpg';
import automotiveClientMobile from '../../assets/automotive_client_mobile.png';
import grayMobile from '../../assets/gray_mobile.png';


const REASONS = [
  { id: '01', title: 'IMR & Commercial Co-op Management', bullets: ['Turn-Key Agency – IMR Management', 'Manage retail & commercial co-op funds'] },
  { id: '02', title: 'Media Planning & Buying', bullets: ['TV, Radio, Outdoor, Digital, OTT', 'Paid Search, Social Media, Display'] },
  { id: '03', title: 'Creative Production', bullets: ['Custom Dealer Creative utilizing IMR funds', 'Social content & quick-turn promotions'] },
  { id: '04', title: 'Audience Targeting & Data', bullets: ['Reach in-market shoppers & fleet buyers', 'CRM activation, retargeting, geo-targeting'] },
  { id: '05', title: 'Incentives & Reporting', bullets: ['Manage offers in real time', 'Track campaign performance & optimization'] },
];

const CONTACTS = [
  { name: 'Ben Pierce',    email: 'bpierce@martinadvertising.com',  avatar1x: benHeadshot,    avatar2x: benHeadshot },
  { name: 'Carson Griffis',email: 'cgriffis@martinadvertising.com', avatar1x: carsonHeadshot, avatar2x: carsonHeadshot },
  { name: 'Dylan Haynes',  email: 'dhaynes@martinadvertising.com',  avatar1x: dylanHeadshot,  avatar2x: dylanHeadshot },
  { name: 'Cali Persaud',  email: 'cpersaud@martinadvertising.com', avatar1x: caliHeadshot,   avatar2x: caliHeadshot },
];

const IMG = {
  hero: auto_header,
  blueCar: blueCadillac,
  mobileA: automotiveClientMobile,
  mobileB: grayMobile,
};

/* NEW: Avatar with fallback + anti-blurry setup
   - FIX: previous component had mismatched braces and no return path
   - Ensures crispness by constraining width/height and using srcSet
*/
const InitialsAvatar = ({ name, src, srcSet }) => {
  const [error, setError] = React.useState(false);

  const initials = React.useMemo(
    () => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
    [name]
  );

  if (error || !src) {
    return (
      <div className={s.avatarFallback} aria-hidden="true">
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      // TIP: provide true 2x assets here to prevent graininess
      srcSet={srcSet}
    //   sizes="(min-width:1024px) 91px, 121px"
      width={91}
      height={121}
      loading="lazy"
      decoding="async"
      alt={name}
      className={s.avatar}
      onError={() => setError(true)}
    />
  );
};

/* Main component */
export default function AutomotiveClientPage() {
  useSeo({
    title: 'Automotive Advertising & Co-op Management',
    description: 'Turn-key automotive agency: IMR and co-op management, media planning, creative production, audience targeting, and reporting.',
    path: '/automotive-client',
  });

  return (
    <main className={s.main}>
      {/* Masthead / hero */}
      <section className={s.hero} aria-label="Automotive hero">
        <img src={IMG.hero} alt="" className={`${s.heroImg} ${s.heroDesktop}`} />

        {/* [M2] Mobile hero: stacked images (shown on mobile only) */}
        <div className={s.heroMobile} aria-hidden="true">
          <img src={IMG.mobileA} alt="" className={s.heroMobileImg} />
          <img src={IMG.mobileB} alt="" className={s.heroMobileImg} />
        </div>
    </section>

      {/* Below-masthead container */}
      <section className={s.below} aria-label="Overview and reasons">
        {/* Top row: copy card + red promo (overlaps hero via negative margin in CSS) */}
        <div className={s.topRow}>
          <div className={s.copyCard}>
            <p className={s.copyBody}>
              Martin Advertising drives retail and commercial sales with targeted, data-driven marketing that maximizes your ad and co-op dollars. As a turnkey agency, we handle strategy, media buying, production, and reporting — plus deliver personalized account support and exclusive custom creative you won’t find elsewhere.
            </p>
          </div>

          <div className={s.redPromo}>
            <h2 className={s.promoTitle}>
              It’s like having all the experts but none of the overhead.
            </h2>
          </div>
        </div>

        {/* Bottom row: grey reasons panel (left) + right column (blue car, contacts) */}
        <div className={s.bottomRow}>
          <div className={s.whyPanel}>
            <h2 className={s.whyTitle}>Why Martin?</h2>
            <p className={s.subline}>(Because you want to sell more cars)</p>

            {REASONS.map((r) => (
              <div key={r.id} className={s.reason}>
                <div className={s.pill}>{r.id}</div>
                <div>
                  <h3 className={s.reasonTitle}>{r.title}</h3>
                  <ul className={s.reasonBullets}>
                    {r.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className={s.rightCol}>
            <img
              src={IMG.blueCar}
              alt="Vehicle detail"
              className={s.blueCar}
              loading="lazy"
              decoding="async"
            />

            <div className={s.contacts}>
                <h3>Meet Your Account Team</h3>
              <ul className={s.contactList}>
                {CONTACTS.map((c) => (
                  <li key={c.email} className={s.contact}>
                      <div className={s.contactInfo}>
                      <span className={s.contactName}>{c.name}</span>
                      <a href={`mailto:${c.email}`} className={s.contactEmail}>
                        {c.email}
                      </a>
                    </div>
                    <InitialsAvatar
                      name={c.name}
                      src={c.avatar1x}
                      srcSet={`${c.avatar1x} 1x, ${c.avatar2x} 2x`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
